/** Skill ranks, costs, build labels and presets (docs/game-design.md §5). */
import { PROGRESSION } from '../config/progression';
import {
  MAX_RANK,
  RANK_COST,
  SKILL_GROUPS,
  SKILL_IDS,
  SKILLS,
  type SkillGroup,
  type SkillId,
  type SkillRanks,
} from '../config/skills';

export function rankCost(nextRank: number): number {
  if (nextRank < 1 || nextRank > MAX_RANK) return Infinity;
  return RANK_COST[nextRank as 1] ?? Infinity;
}

/** Points spent to reach `rank` from 0. */
export function pointsForRank(rank: number): number {
  let sum = 0;
  for (let r = 1; r <= rank; r++) sum += rankCost(r);
  return sum;
}

export function spentPoints(ranks: SkillRanks): number {
  return SKILL_IDS.reduce((a, id) => a + pointsForRank(ranks[id]), 0);
}

export function groupPoints(ranks: SkillRanks): Record<SkillGroup, number> {
  const out = { striking: 0, grappling: 0, defense: 0, engine: 0 };
  for (const s of SKILLS) out[s.group] += pointsForRank(ranks[s.id]);
  return out;
}

export type BuildLabel = 'striker' | 'wrestler' | 'tank' | 'engine' | 'balanced' | 'none';

const GROUP_LABEL: Record<SkillGroup, BuildLabel> = {
  striking: 'striker',
  grappling: 'wrestler',
  defense: 'tank',
  engine: 'engine',
};

/** The group with most spent points; Balanced when no group has more than 35%. */
export function buildLabel(ranks: SkillRanks): BuildLabel {
  const g = groupPoints(ranks);
  const total = g.striking + g.grappling + g.defense + g.engine;
  if (total === 0) return 'none';
  let best: SkillGroup = 'striking';
  for (const k of SKILL_GROUPS) if (g[k] > g[best]) best = k;
  if (g[best] / total <= PROGRESSION.balancedShare) return 'balanced';
  return GROUP_LABEL[best];
}

export type PresetId = 'striker' | 'wrestler' | 'tank' | 'balanced';

/** Skills each preset raises, in priority order (placeholder orders). */
export const PRESETS: Record<PresetId, readonly SkillId[]> = {
  striker: ['power', 'speed', 'reach', 'cardio'],
  wrestler: ['takedown', 'escape', 'chin', 'cardio'],
  tank: ['guard', 'chin', 'footwork', 'cardio'],
  balanced: SKILL_IDS,
};

/**
 * Spends up to `points` on a preset: repeatedly raises the lowest-ranked skill of the preset
 * (ties by list order) that is affordable. Returns the new ranks and points used.
 */
export function applyPreset(ranks: SkillRanks, points: number, preset: PresetId) {
  const next = { ...ranks };
  let left = points;
  for (;;) {
    let pick: SkillId | null = null;
    for (const id of PRESETS[preset]) {
      if (next[id] >= MAX_RANK || rankCost(next[id] + 1) > left) continue;
      if (pick === null || next[id] < next[pick]) pick = id;
    }
    if (pick === null) break;
    left -= rankCost(next[pick] + 1);
    next[pick]++;
  }
  return { ranks: next, used: points - left };
}
